// Constants
const FeatureToggleFlags = Object.freeze({
  DISABLED: 'disabled',
  ENABLED: 'enabled'
});

// Main Manager Class
class ProductPageManager {
  constructor() {
    this.ytHandler = new YouTubePlayerHandler();
    this.fancyboxHandler = new VideoThumbnailFancyboxHandler();
    this.tooltipManager = new TooltipManager();
    this.stockDisplay = new StockAvailabilityDisplay();
    this.imageZoom = new ImageZoomEffect();
    this.variantHandler = new VariantHandler();
    this.productScroller = new ProductThumbsScroller();
    this.reviewsScroller = new ReviewsScroller();
  }

  init() {
    this.ytHandler.init();
    this.fancyboxHandler.init();
    this.tooltipManager.init();
    this.stockDisplay.init();
    this.imageZoom.init();
    this.variantHandler.init();
    this.productScroller.init();
    this.reviewsScroller.init();
    ProductPageManager.disablePdpLoading();
  }

  static disablePdpLoading() {
    document.querySelectorAll('[data-pdp-loading]')?.forEach(element => {
      element.style.pointerEvents = 'unset';
    });
  }
}

// YouTube Player Handler
class YouTubePlayerHandler {
  constructor() {
    this.trackYtPlayerState = {};
  }

  init() {
    document.addEventListener('yt-player-event', this.ytHandler.bind(this));
  }

  ytHandler(e) {
    const ytPlayer = e.target.parentElement.querySelector('iframe');
    if (ytPlayer.getAttribute('data-video-type') === 'youtube') {
      const id = ytPlayer.getAttribute('id');
      const player = new YT.Player(ytPlayer);

      if (!(id in this.trackYtPlayerState)) {
        for (let key in this.trackYtPlayerState) {
          delete this.trackYtPlayerState[key];
        }
        this.trackYtPlayerState[id] = true;
      }

      if (this.trackYtPlayerState[id]) {
        player.pauseVideo();
        this.trackYtPlayerState[id] = false;
      } else {
        player.playVideo();
        this.trackYtPlayerState[id] = true;
      }
    }
  }
}

// Video Thumbnail Fancybox Handler
class VideoThumbnailFancyboxHandler {
  init() {
    const GLOBAL_THUMBNAIL_FANCYBOX = FeatureToggleFlags.DISABLED;
    const videoThumbnails = document.querySelectorAll('[data-thumbnail-video-modal]');

    if (GLOBAL_THUMBNAIL_FANCYBOX === FeatureToggleFlags.ENABLED && videoThumbnails && videoThumbnails.length > 0) {
      videoThumbnails.forEach((videoThumbnail) => {
        if (window.innerWidth < 768) return;

        let fancyBoxTarget = videoThumbnail;

        if (window.innerWidth > 768) {
          const dataPointerEventsNone = videoThumbnail.querySelector('[data-thumbnail-pointer-events="none"]');
          if (dataPointerEventsNone) {
            dataPointerEventsNone.style.pointerEvents = 'none';
          }
        }

        const playIcon = videoThumbnail.querySelector('.custom-play-icon');
        if (playIcon) {
          fancyBoxTarget = playIcon;
        }

        fancyBoxTarget.addEventListener('click', () => {
          const videoFileURL = videoThumbnail.getAttribute('data-video-media-source');
          const isMP4 = videoFileURL.toLowerCase().includes('.mp4');

          const fancyboxConfig = isMP4
            ? {
                src: videoFileURL,
                type: 'html',
                html: `<video controls playsinline autoplay>
                    <source src="${videoFileURL}" type="video/mp4">
                  </video>`,
                width: '100%',
                height: '100%',
              }
            : {
                src: videoFileURL,
                type: 'video',
                video: {
                  format: 'mp4',
                  autoplay: true,
                },
              };

          Fancybox.show([fancyboxConfig]);
        });
      });
    }
  }
}

// Tooltip Manager
class TooltipManager {
  init() {
    tippy('#trainingVideoEndIconId', {
      content: '',
      allowHTML: true,
      onShow(instance) {
        const popupContent = document.getElementById('trainingVideoEndIconId').getAttribute('data-popup-content');
        instance.setContent(popupContent);
      },
    });

    try {
      const tooltipElement = document.querySelector('.question-mark-tooltip-icon-wrapper');
      if (tooltipElement) {
        const popupContent = document.getElementById('pro-variant-tooltip-content').innerHTML;

        if (popupContent && popupContent.trim() !== '') {
          tippy('.question-mark-tooltip-icon-wrapper', {
            content: popupContent,
            allowHTML: true,
            interactive: true,
          });
        }
      }
    } catch (error) {}
  }
}

// Stock Availability Display
class StockAvailabilityDisplay {
  init() {
    try {
      // Check if the NodeList is not empty and select the desired element
      const stockAvailableSpans = document.querySelectorAll('.product-stock-availability--text');
      if (stockAvailableSpans.length > 0) {
        const selectStockSpan = 0;
        const stockAvailableSpan = stockAvailableSpans[selectStockSpan];

        // Safely retrieve attributes and handle potential missing values
        const stockAvailableCount = parseInt(stockAvailableSpan.getAttribute('data-stock-availability') || '0', 10);
        const stockNumberColor = stockAvailableSpan.getAttribute('data-stock-number-color') || '';
        const stockTextColor = stockAvailableSpan.getAttribute('data-stock-text-color') || '';
        const stockFontSize = stockAvailableSpan.getAttribute('data-stock-font-size') || '';
        const stockFontWeight = stockAvailableSpan.getAttribute('data-stock-font-weight') || '';

        // Call updateStockText if all necessary variables are defined
        if (stockAvailableSpan && !isNaN(stockAvailableCount)) {
          this.updateStockText(stockAvailableCount, stockAvailableSpan, stockNumberColor, stockTextColor, stockFontSize, stockFontWeight);
        }

        // Check if Shopify design mode is available
        if (window && window.Shopify && window.Shopify.designMode) {
          this.updateStockText(stockAvailableCount, stockAvailableSpan, stockNumberColor, stockTextColor, stockFontSize, stockFontWeight);
        }

        // Add event listener if stockAvailableSpan is defined
        if (stockAvailableSpan) {
          document.addEventListener('variant:change', (event) => {
            const inventoryQuantity = (event.detail && event.detail.variant && event.detail.variant.inventory_quantity) || 0;
            this.updateStockText(inventoryQuantity, stockAvailableSpan, stockNumberColor, stockTextColor, stockFontSize, stockFontWeight);

            if (window && window.Shopify && window.Shopify.designMode) {
              this.updateStockText(inventoryQuantity, stockAvailableSpan, stockNumberColor, stockTextColor, stockFontSize, stockFontWeight);
            }
          });
        }
      }
    } catch (error) {
      console.error('An error occurred in StockAvailabilityDisplay.init:', error);
    }
  }

  updateStockText(count, stockAvailableSpan, stockNumberColor, stockTextColor, stockFontSize, stockFontWeight) {
    try {
      if (!stockAvailableSpan) return;

      if (count > 50) {
        stockAvailableSpan.innerHTML = '';
      } else if (count > 10) {
        stockAvailableSpan.innerHTML = `
          <span style="color: ${stockNumberColor}; 
                        font-size: ${stockFontSize}px; 
                        font-weight: ${stockFontWeight};">
            ${count}
          </span> 
          <span style="font-size: ${stockFontSize}px; 
                        font-weight: ${stockFontWeight};
                        color: ${stockTextColor};">
            Available
          </span>`;
      } else if (count > 0) {
        stockAvailableSpan.innerHTML = `
          <span style="font-size: ${stockFontSize}px; 
                        font-weight: ${stockFontWeight};
                        color: ${stockTextColor};">
            Hurry only 
          </span>
          <span style="color: ${stockNumberColor}; 
                        font-size: ${stockFontSize}px; 
                        font-weight: ${stockFontWeight};">
            ${count}
          </span> 
          <span style="font-size: ${stockFontSize}px; 
                        font-weight: ${stockFontWeight}; 
                        color: ${stockTextColor};">
            left in stock
          </span>`;
      } else {
        stockAvailableSpan.textContent = '';
      }
    } catch (error) {
      console.error('An error occurred in StockAvailabilityDisplay.updateStockText:', error);
    }
  }
}

// Image Zoom Effect
class ImageZoomEffect {
  init() {
    const images = document.querySelectorAll('.custom_zoom_class');

    images.forEach((image) => {
      image.addEventListener('mousemove', (event) => {
        const rect = image.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const percentX = (x / rect.width) * 100;
        const percentY = (y / rect.height) * 100;

        image.style.transformOrigin = `${percentX}% ${percentY}%`;
        image.style.transform = 'scale(2)';
      });

      image.addEventListener('mouseout', () => {
        image.style.transform = 'scale(1)';
      });
    });
  }
}

// Variant Handler
class VariantHandler {
  init() {
    const levelFieldset = document.querySelector('fieldset.variant-input-wrap[data-handle="level"]');
    if (levelFieldset) {
      const productLevelSwatches = levelFieldset.querySelectorAll('.variant-input');

      const mainLevel = document.querySelector('[data-handle="level"]').parentElement.querySelector('span[data-level-label]');
      const levelLabelMain = mainLevel.parentElement.parentElement.querySelector('.variant__label').querySelector('span[data-level-label]');
      let storedLevelLabel = mainLevel.textContent.trim();

      productLevelSwatches.forEach((level) => {
        const levelValue = level.getAttribute('data-value');

        level.addEventListener('click', () => {
          storedLevelLabel = levelValue;
          levelLabelMain.textContent = levelValue;
        });

        level.addEventListener('mouseenter', (event) => {
          const label = event.target.parentElement.parentElement.querySelector('.variant__label');
          const currentLabel = label.querySelector('span[data-level-label]');
          currentLabel.textContent = levelValue;
        });

        level.addEventListener('mouseleave', (event) => {
          const label = event.target.parentElement.parentElement.querySelector('.variant__label');
          const currentLabel = label.querySelector('span[data-level-label]');
          currentLabel.textContent = storedLevelLabel;
        });
      });
    }

    const variantFieldset = document.querySelector('fieldset.variant-input-wrap[data-handle="color"]');
    if (variantFieldset) {
      const productColorSwatches = variantFieldset.querySelectorAll('.variant-input');

      const mainColorLabel = document.querySelector('[data-handle="color"]').parentElement.querySelector('span[data-color-label]');
      let storedColorLabel = mainColorLabel.textContent.trim();

      productColorSwatches.forEach((colorSwatch) => {
        const colorValue = colorSwatch.getAttribute('data-value');

        colorSwatch.addEventListener('click', () => {
          storedColorLabel = colorValue;
        });

        colorSwatch.addEventListener('mouseenter', (event) => {
          const label = event.target.parentElement.parentElement.querySelector('.variant__label');
          const currentLabel = label.querySelector('span[data-color-label]');
          currentLabel.textContent = colorValue;
        });

        colorSwatch.addEventListener('mouseleave', (event) => {
          const label = event.target.parentElement.parentElement.querySelector('.variant__label');
          const currentLabel = label.querySelector('span[data-color-label]');
          currentLabel.textContent = storedColorLabel;
        });
      });
    }
  }
}

// Product Thumbs Scroller
class ProductThumbsScroller {
  init() {
    const scroller = document.querySelector('[data-product-thumbs-scroller]');
    if (!scroller) return;

    let isDragging = false;
    let startX;
    let scrollLeft;
    let originalScrollBehavior;

    scroller.addEventListener('mousedown', (e) => {
      isDragging = true;
      scroller.classList.add('active');
      startX = (e.pageX || e.touches[0].pageX) - scroller.offsetLeft;
      scrollLeft = scroller.scrollLeft;

      originalScrollBehavior = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
    }, { passive: false });

    window.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        scroller.classList.remove('active');
        scroller.style.scrollBehavior = originalScrollBehavior;
        scroller.style.pointerEvents = 'auto';
      }
    });

    window.addEventListener('mousemove', (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();

        const x = (e.pageX || e.touches[0].pageX) - scroller.offsetLeft;
        const walk = x - startX;

        requestAnimationFrame(() => {
          scroller.scrollLeft = scrollLeft - walk;
        });
      }
    }, { passive: false });

    scroller.addEventListener('click', (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true });
  }
}

// Reviews Scroller
class ReviewsScroller {
  init() {
    if (document.body.classList.contains('template-product')) {
      window.addEventListener('load', () => {
        this.addTagToYotpo();
        this.addIdToYotpoWidget();

        const scrollToReviewLink = document.querySelector('.scroll-to-reviews');
        if (scrollToReviewLink) {
          scrollToReviewLink.addEventListener('click', this.smoothScroll.bind(this));
        }
      });
    }
  }

  addTagToYotpo() {
    const productBlock = document.querySelector('.product-block > .yotpo');
    if (productBlock) {
      const newTag = document.createElement('a');
      const productId_stars = productBlock.getAttribute('data-product-id');
      newTag.classList.add("scroll-to-reviews");
      newTag.setAttribute('href', `#scroll-to-reviews-${productId_stars}`);
      productBlock.appendChild(newTag);
    }
  }

  addIdToYotpoWidget() {
    const yotpoWidget = document.querySelector('.yotpo.yotpo-main-widget');
    if (yotpoWidget) {
      const productId = yotpoWidget.getAttribute('data-product-id');
      if (productId) {
        yotpoWidget.id = `scroll-to-reviews-${productId}`;
      }
    }
  }

  smoothScroll(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute('href').substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - 300;

      targetElement.scrollIntoView({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const productPageManager = new ProductPageManager();
  productPageManager.init();

  if (window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => productPageManager.init());
    document.addEventListener('shopify:section:reorder', () => productPageManager.init());
    document.addEventListener('shopify:section:unload', () => productPageManager.init());
    document.addEventListener('shopify:block:select', () => productPageManager.init());
    document.addEventListener('shopify:block:deselect', () => productPageManager.init());
  }
});
